import React from "react";
import { formatPrice, formatPriceAmount } from "../../../utils/saleUtils";
import { Link } from "react-router-dom";
import { FaEdit, FaLink, FaBoxOpen } from "react-icons/fa";
import { MdDelete } from "react-icons/md";
import { ListingStatusIcon } from "./ListingStatusIcon";
import { ListingActionsMenu } from "./ListingActionsMenu";
import { useIndexPageData } from "../../../contextapi/Indexpagedata";
import { isBundleListing } from "../utils/helpers";

const STAT_ITEMS = [
  { key: "price", label: "Price", getValue: (item) => formatPrice(item?.price) },
  { key: "views", label: "Views", getValue: (item) => item?.view_count || "0" },
  {
    key: "totalEarned",
    label: "Total Earned",
    getValue: (item) => formatPriceAmount(item?.totalEarned),
    valueClass: "detailed-listing-card__stat-value--success",
  },
  {
    key: "earnedMonth",
    label: "Earned This Month",
    getValue: (item) => formatPriceAmount(item?.totalEarnedThisMonth),
    valueClass: "detailed-listing-card__stat-value--success",
  },
  {
    key: "totalSales",
    label: "Total Sales",
    getValue: (item) => item?.totalSales || 0,
  },
  {
    key: "salesMonth",
    label: "Sales This Month",
    getValue: (item) => item?.totalSalesThisMonth || 0,
  },
];

export const DetailedListingCard = ({
  item,
  menuAnchor,
  selectedItemId,
  selectionModeActive = false,
  selectedListingIds = [],
  selectedBundleIds = [],
  isSelectedAll = false,
  onToggleSelect,
  onMenuClick,
  onMenuClose,
  onEdit,
  onDelete,
  onOpenBundle,
  onSharableLink,
  onPublishModal,
  showMoveToFolder = false,
  onMoveToFolderClick,
  showRemoveFromFolder = false,
  onRemoveFromFolderClick,
  openBundleAsLink = false,
}) => {
  const { profiledata } = useIndexPageData();
  const isBundle = isBundleListing(item);
  const showCheckbox = selectionModeActive;
  const isSelected = isBundle
    ? selectedBundleIds.includes(item?.id)
    : isSelectedAll || selectedListingIds.includes(item?.id);

  const imageSrc = isBundle
    ? item?.bundle_thumbnails?.[0]?.image
    : item?.use_main_file_as_thumbnail
      ? item?.compressed_file
      : item?.thumbnails?.[0]?.image;

  const imageLink = isBundle ? `/bundle?id=${item?.id}` : `/mockup?id=${item.id}`;

  const renderBundleActions = () => (
    <>
      {openBundleAsLink ? (
        <Link
          to={`/bundle?id=${item?.id}`}
          className="detailed-listing-card__primary-action"
        >
          <FaBoxOpen aria-hidden="true" />
          <span>Open</span>
        </Link>
      ) : (
        <button
          type="button"
          className="detailed-listing-card__primary-action"
          onClick={() => onOpenBundle(item?.id)}
        >
          <FaBoxOpen aria-hidden="true" />
          <span>Open</span>
        </button>
      )}
      <button
        type="button"
        className="detailed-listing-card__icon-btn detailed-listing-card__icon-btn--edit"
        onClick={() => onEdit(item)}
        aria-label={`Edit ${item?.title || "bundle"}`}
        title="Edit bundle"
      >
        <FaEdit aria-hidden="true" />
      </button>
      <button
        type="button"
        className="detailed-listing-card__icon-btn detailed-listing-card__icon-btn--danger"
        onClick={() => onDelete(item?.id, true)}
        aria-label={`Delete ${item?.title || "bundle"}`}
        title="Delete bundle"
      >
        <MdDelete aria-hidden="true" />
      </button>
    </>
  );

  const renderMockupActions = () => (
    <>
      <button
        type="button"
        className="detailed-listing-card__primary-action"
        onClick={() => onEdit(item)}
      >
        <FaEdit aria-hidden="true" />
        <span>Edit</span>
      </button>
      <button
        type="button"
        className="detailed-listing-card__icon-btn detailed-listing-card__icon-btn--danger"
        onClick={() => onDelete(item?.id)}
        aria-label={`Delete ${item?.title || "listing"}`}
        title="Delete listing"
      >
        <MdDelete aria-hidden="true" />
      </button>
      {profiledata?.is_seller_pro_active && (
        <button
          type="button"
          className="detailed-listing-card__icon-btn detailed-listing-card__icon-btn--link"
          disabled={!item?.sharable_link_enabled}
          aria-label="Copy sharable link"
          title={
            item?.sharable_link_enabled
              ? "Copy sharable link"
              : "Enable sharable link from the status menu first"
          }
          onClick={(e) => {
            e.preventDefault();
            e.stopPropagation();
            if (!item?.sharable_link_enabled) return;
            onSharableLink(e, item?.id, item?.sharable_link_id);
          }}
        >
          <FaLink aria-hidden="true" />
        </button>
      )}
    </>
  );

  return (
    <article className="detailed-listing-card">
      {showCheckbox && (
        <div
          className="detailed-listing-card__checkbox"
          onClick={(e) => e.stopPropagation()}
        >
          <input
            type="checkbox"
            className="form-check-input border-2"
            checked={isSelected}
            onChange={() => onToggleSelect?.(item?.id, isBundle)}
            aria-label={`Select ${item?.title}`}
          />
        </div>
      )}

      <div className="detailed-listing-card__layout">
        <div className="detailed-listing-card__media">
          <Link to={imageLink} className="detailed-listing-card__image-link">
            <img
              src={imageSrc}
              alt={item?.title}
              className="detailed-listing-card__image"
            />
          </Link>
          {!isBundle && (
            <>
              <ListingStatusIcon
                item={item}
                onClick={(e) => onMenuClick(e, item)}
              />
              <ListingActionsMenu
                anchorEl={menuAnchor}
                open={selectedItemId === item.id}
                onClose={onMenuClose}
                item={item}
                onPublish={onPublishModal}
                onUnpublish={onPublishModal}
                onRequestUnblock={onPublishModal}
                onEnableSharableLink={onPublishModal}
                onDisableSharableLink={onPublishModal}
                showMoveToFolder={showMoveToFolder}
                onMoveToFolderClick={onMoveToFolderClick}
                onEdit={onEdit}
              />
            </>
          )}
        </div>

        <div className="detailed-listing-card__content">
          <div className="detailed-listing-card__info">
            <h5 className="detailed-listing-card__title">{item?.title}</h5>
            {item?.short_description && (
              <p className="detailed-listing-card__description">
                {item.short_description}
              </p>
            )}
            {showRemoveFromFolder && onRemoveFromFolderClick && (
              <button
                type="button"
                className="btn btn-sm btn-outline-secondary detailed-listing-card__remove-folder"
                onClick={() =>
                  onRemoveFromFolderClick(item?.id, isBundle)
                }
              >
                Remove from folder
              </button>
            )}
          </div>

          <div className="detailed-listing-card__actions">
            {isBundle ? renderBundleActions() : renderMockupActions()}
          </div>
        </div>

        <div className="detailed-listing-card__stats">
          {STAT_ITEMS.map(({ key, label, getValue, valueClass }) => (
            <div key={key} className="detailed-listing-card__stat">
              <span className="detailed-listing-card__stat-label">{label}</span>
              <span
                className={`detailed-listing-card__stat-value${
                  valueClass ? ` ${valueClass}` : ""
                }`}
              >
                {getValue(item)}
              </span>
            </div>
          ))}
        </div>
      </div>
    </article>
  );
};
